import axios from 'axios';
import { Game, Genre, Platform, Franchise, Collection, GameEngine, Theme as GameTheme, Company } from '../types/Game';
import { Review, ReviewDetail } from '../types/Review';
import { AxiosResponse } from 'axios';
import { UserProfile } from '../types/UserProfile';
import { useNavigate } from 'react-router-dom';

interface SearchFilters {
  genres?: number[];
  platforms?: number[];
  themes?: number[];
  year?: number | '';
  order?: string;
}

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const onRrefreshed = (token: string) => {
  refreshSubscribers.map((callback) => callback(token));
};

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // ゲスト利用で許可されていない機能（バックエンドが code: 'guest_forbidden' で403を返す）
    if (error.response?.status === 403 && error.response?.data?.code === 'guest_forbidden') {
      window.dispatchEvent(new CustomEvent('guestUpgradeRequired'));
      return Promise.reject(error);
    }
    // ゲストが既存アカウントでログインしようとした（バックエンドが409で止める）。
    // そのままログインさせるとゲストのライブラリが到達不能になるため、破棄が必要な旨を案内する
    if (error.response?.status === 409 && error.response?.data?.code === 'guest_existing_account') {
      window.dispatchEvent(new CustomEvent('guestExistingAccount'));
      return Promise.reject(error);
    }
    if (error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((token: string) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        await logout();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        });
        const newToken = response.data.access;
        localStorage.setItem('token', newToken);
        // ROTATE_REFRESH_TOKENS=True のため更新時に新しいrefreshトークンも返る。
        // これを保存することで有効期限がスライドし、使い続ける限りログインを維持できる。
        if (response.data.refresh) {
          localStorage.setItem('refreshToken', response.data.refresh);
        }
        isRefreshing = false;
        onRrefreshed(newToken);
        refreshSubscribers = [];
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
        return api(originalRequest);
      } catch (err) {
        isRefreshing = false;
        await logout();
        return Promise.reject(err);
      }
    }
    return Promise.reject(error);
  }
);

export const getGames = () => api.get('/games/');
export const getRecentGames = (): Promise<{ data: { results: Game[] } }> => api.get('/games/recent/?limit=10');
export const getPopularGames = (): Promise<{ data: { results: Game[] } }> => api.get('/games/popular/?limit=10');
export const getGameDetails = async (game_id: number) => {
  const response = await axios.get(`${API_BASE_URL}/games/${game_id}/`);
  return response.data;
};
export const searchGames = (query: string, page: number, filters: SearchFilters = {}): Promise<{ results: Game[], count: number }> => {
  const params = new URLSearchParams({
    q: query,
    page: page.toString(),
  });

  if (filters.genres && filters.genres.length > 0) {
    params.append('genres', filters.genres.join(','));
  }
  if (filters.platforms && filters.platforms.length > 0) {
    params.append('platforms', filters.platforms.join(','));
  }
  if (filters.themes && filters.themes.length > 0) {
    params.append('themes', filters.themes.join(','));
  }
  if (filters.year) {
    params.append('year', filters.year.toString());
  }
  if (filters.order && filters.order !== '') {
    params.append('order', filters.order);
  }

  console.log('Sending request with params:', params.toString());

  return api.get(`/games/search/?${params.toString()}`).then(response => {
    console.log('API Response:', JSON.stringify(response.data, null, 2));
    return {
      results: response.data.results || [],
      count: response.data.count || 0
    };
  });
};

// 新しく追加する関数
export const getPlatforms = (): Promise<Platform[]> => 
  api.get('/platforms/').then(response => response.data);

export const getGenres = async (): Promise<Genre[]> => {
  const response = await api.get('/genres/');
  return response.data;
};

export const getGamesByPlatform = async (id: string, page: number = 1) => {
  const response = await axios.get(`${API_BASE_URL}/games/by_platform/?id=${id}&page=${page}`);
  return response.data;
};

export const getGamesByGenre = async (id: string, page: number = 1) => {
  const response = await axios.get(`${API_BASE_URL}/games/by_genre/?id=${id}&page=${page}`);
  return response.data;
};

export const getFranchises = (): Promise<Franchise[]> =>
  api.get('/franchises/').then(response => response.data);

export const getCollections = (): Promise<Collection[]> =>
  api.get('/collections/').then(response => response.data);

export const getGamesByFranchise = async (id: string, page: number = 1) => {
  const response = await axios.get(`${API_BASE_URL}/games/by_franchise/?id=${id}&page=${page}`);
  return response.data;
};

export const getGamesByCollection = async (id: string, page: number = 1) => {
  const response = await axios.get(`${API_BASE_URL}/games/by_collection/?id=${id}&page=${page}`);
  return response.data;
};

export const getGameEngines = (): Promise<GameEngine[]> =>
  api.get('/game-engines/').then(response => response.data);

export const getThemes = async (): Promise<GameTheme[]> => {
  const response = await api.get('/themes/');
  return response.data;
};

export const getGamesByGameEngine = async (id: string, page: number = 1) => {
  const response = await axios.get(`${API_BASE_URL}/games/by_game_engine/?id=${id}&page=${page}`);
  return response.data;
};

export const getGamesByTheme = async (id: string, page: number = 1) => {
  const response = await axios.get(`${API_BASE_URL}/games/by_theme/?id=${id}&page=${page}`);
  return response.data;
};

export const getCompanies = (): Promise<Company[]> =>
  api.get('/companies/').then(response => response.data);

export const getGamesByCompany = async (id: string, page: number = 1) => {
  const response = await axios.get(`${API_BASE_URL}/games/by_company/?id=${id}&page=${page}`);
  return response.data;
};

export const getUserProfile = async (): Promise<AxiosResponse<UserProfile>> => {
  return await api.get('/profile/');
};

export const updateUserProfile = (data: any) => api.patch('/profile/', data);

export const checkProfileCompletion = async () => {
  try {
    const response = await api.get('/profile/completion/');
    return response.data.is_complete;
  } catch (error) {
    console.error('プロフィール完成度チェックに失敗しました', error);
    return false;
  }
};

export const logout = async () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    // カスタムイベントを発火してヘッダーの状態を更新
    const event = new CustomEvent('logout');
    window.dispatchEvent(event);
  } catch (error) {
    console.error('ログアウトに失敗しました', error);
  }
};

// ゲストアカウントを作成してトークンを取得する（クッション画面から呼ばれる）
export const createGuestAccount = async () => {
  const response = await axios.post(`${API_BASE_URL}/auth/guest/`, {});
  return response.data;
};

// 現在ゲストとしてログインしているかどうか（localStorageのuser情報で判定）
export const isGuestUser = (): boolean => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.is_guest === true;
  } catch {
    return false;
  }
};

export const setGameStatus = async (gameId: number, status: number | null) => {
  try {
    const response = await api.post(`/user-game-status/set_status/`, { 
      game_id: gameId, 
      status: status === null ? 'delete' : status 
    });
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.error) {
      throw new Error(error.response.data.error);
    } else {
      throw new Error('ゲームステータスの更新に失敗しました');
    }
  }
};

export const setGameReview = async (
  gameId: number, 
  score: number | null, 
  reviewText: string | null, 
  playStartDate: string | null, 
  playEndDate: string | null, 
  playTime: number | null, 
  scoreTags: number[],
  platform: number[],
  scoreVisual: number | null = null,
  scoreSound: number | null = null,
  scoreGameplay: number | null = null,
  scoreNarrative: number | null = null,
  scoreOriginality: number | null = null
) => {
  try {
    const response = await api.post(`/user-game-status/set_review/`, { 
      game_id: gameId, 
      score, 
      review_text: reviewText, 
      play_start_date: playStartDate, 
      play_end_date: playEndDate, 
      play_time: playTime, 
      score_tags: scoreTags,
      platform: platform,
      score_visual: scoreVisual,
      score_sound: scoreSound,
      score_gameplay: scoreGameplay,
      score_narrative: scoreNarrative,
      score_originality: scoreOriginality
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('レビューの更新に失敗しました');
  }
};

export const getUserGameStatus = async (gameId: number): Promise<{ 
  status: number | null, 
  score: number | null, 
  reviewText: string | null, 
  playStartDate: string | null, 
  playEndDate: string | null, 
  playTime: number | null, 
  subStatus: number[],
  scoreTags: number[],
  platform: number[],
  scoreVisual: number | null,
  scoreSound: number | null,
  scoreGameplay: number | null,
  scoreNarrative: number | null,
  scoreOriginality: number | null
}> => {
  try {
    const response = await api.get(`/user-game-status/get_status/`, { params: { game_id: gameId } });
    return {
      status: response.data.status,
      score: response.data.score,
      reviewText: response.data.review_text,
      playStartDate: response.data.play_start_date,
      playEndDate: response.data.play_end_date,
      playTime: response.data.play_time,
      subStatus: response.data.sub_status || [],
      scoreTags: response.data.score_tags || [],
      platform: response.data.platform || [],
      scoreVisual: response.data.score_visual || null,
      scoreSound: response.data.score_sound || null,
      scoreGameplay: response.data.score_gameplay || null,
      scoreNarrative: response.data.score_narrative || null,
      scoreOriginality: response.data.score_originality || null
    };
  } catch (error) {
    console.error('ゲームステータスの取得に失敗しました', error);
    return { 
      status: null, 
      score: null, 
      reviewText: null, 
      playStartDate: null, 
      playEndDate: null, 
      playTime: null, 
      subStatus: [], 
      scoreTags: [],
      platform: [],
      scoreVisual: null,
      scoreSound: null,
      scoreGameplay: null,
      scoreNarrative: null,
      scoreOriginality: null
    };
  }
};

export type PlatformFilterType = 'user_platform' | 'release_platform';

export const getGamesByStatus = (status: number, page: number = 1, query: string = '', order: string = 'default', platforms: number[] = [], filterType: PlatformFilterType = 'user_platform', folder?: number | null) =>
  api.get(`/user-game-status/by_status/`, { params: { status, page, query, order, platforms: platforms.length > 0 ? platforms.join(',') : undefined, filter_type: filterType, folder: folder ?? undefined } });

export const getUserProfileByUsername = (username: string): Promise<UserProfile> =>
  api.get(`/profile/${username}/`).then(response => response.data);

export const getGamesByStatusForUser = (username: string, status: number, page: number = 1, query: string = '', order: string = 'default', platforms: number[] = [], filterType: PlatformFilterType = 'user_platform', folder?: number | null) =>
  api.get(`/user-game-status/by_status/${username}/`, { params: { status, page, query, order, platforms: platforms.length > 0 ? platforms.join(',') : undefined, filter_type: filterType, folder: folder ?? undefined } });

export const setGameSubStatus = (gameId: number, subStatus: number[]): Promise<{ game_id: number, sub_status: number[] }> =>
  api.post(`/user-game-status/set_sub_status/`, { game_id: gameId, sub_status: subStatus });

export const getGameReviews = async (gameId: number, page?: number, limit?: number): Promise<{ results: Review[], count: number }> => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());
  if (page) params.append('page', page.toString());
  
  const response = await api.get(`/games/${gameId}/reviews/?${params.toString()}`);
  return {
    results: response.data.results as Review[],
    count: response.data.count
  };
};

export const getPublicUserGameStatus = async (username: string, gameId: number): Promise<{ 
  status: number | null, 
  score: number | null, 
  reviewText: string | null, 
  playStartDate: string | null, 
  playEndDate: string | null, 
  playTime: number | null, 
  subStatus: number[],
  scoreTags: number[],
  platform: number[]
}> => {
  try {
    const response = await api.get(`/user-game-status/${username}/get_status/`, { params: { game_id: gameId } });
    return {
      status: response.data.status,
      score: response.data.score,
      reviewText: response.data.review_text,
      playStartDate: response.data.play_start_date,
      playEndDate: response.data.play_end_date,
      playTime: response.data.play_time,
      subStatus: response.data.sub_status || [],
      scoreTags: response.data.score_tags || [],
      platform: response.data.platform || []
    };
  } catch (error) {
    console.error('パブリックゲームステータスの取得に失敗しました', error);
    return { status: null, score: null, reviewText: null, playStartDate: null, playEndDate: null, playTime: null, subStatus: [], scoreTags: [], platform: [] };
  }
};

export const getTrendingNewReleases = (
  limit: number = 12, 
  offset: number = 0, 
  period: number = 90
): Promise<{ 
  data: { 
    results: Game[]; 
    next: number | null;
    total_count: number;
    period: number;
  } 
}> => api.get(`/trending-new-releases/?limit=${limit}&offset=${offset}&period=${period}`);

export const getAnticipatedGames = (
  limit: number = 12, 
  offset: number = 0, 
  period: number = 90
): Promise<{ 
  data: { 
    results: Game[]; 
    next: number | null;
    total_count: number;
    period: number;
  } 
}> => api.get(`/anticipated-games/?limit=${limit}&offset=${offset}&period=${period}`);

export const getPopular2025Games = (limit: number = 12, offset: number = 0): Promise<{ data: { results: Game[], next: number | null } }> =>
  api.get(`/popular-2025-games/?limit=${limit}&offset=${offset}`);

export const getPopularGamesByYear = (year: number, ratingThreshold: number = 70): Promise<{ data: { results: Game[], next: number | null, year: number } }> => 
  api.get(`/popular-games-by-year/?year=${year}&rating_threshold=${ratingThreshold}`);

export const getStatusLimits = async (): Promise<{ status: number; name: string; baseLimit: number; extensionLimit: number }[]> => {
  try {
    const response = await api.get('/user-profile/status-limits/');
    return response.data;
  } catch (error) {
    console.error('ステータス制限の取得に失敗しました', error);
    throw error;
  }
};

// 一括取得の関数のみ残す
export const getBasicGameStatuses = async (gameIds: number[]): Promise<{ [key: number]: number | null }> => {
  try {
    const response = await api.get(`/user-game-status/get_basic_statuses/`, {
      params: { game_ids: gameIds.join(',') }
    });
    return response.data;
  } catch (error) {
    console.error('基本ゲームステータスの一括取得に失敗しました', error);
    return {};
  }
};

export const getProfileGameStatuses = async (gameIds: number[]): Promise<{
  [key: number]: {
    status: number | null;
    score: number | null;
    review_id?: number | null;
    review_text: string | null;
    play_start_date: string | null;
    play_end_date: string | null;
    play_time: number | null;
    steam_playtime_min: number | null;
    sub_status: number[] | null;
    score_tags: string[] | null;
    platform: number[] | null;
    has_memo?: boolean;
    folder_ids?: number[];
  }
}> => {
  try {
    const response = await api.get(`/user-game-status/get_profile_statuses/`, {
      params: { game_ids: gameIds.join(',') }
    });
    return response.data;
  } catch (error) {
    console.error('プロフィールゲームステータスの一括取得に失敗しました', error);
    return {};
  }
};

export const getPublicGameStatuses = async (username: string, gameIds: number[]): Promise<{
  [key: number]: {
    status: number | null;
    score: number | null;
    review_id?: number | null;
    review_text: string | null;
    play_start_date: string | null;
    play_end_date: string | null;
    play_time: number | null;
    steam_playtime_min: number | null;
    sub_status: number[] | null;
    score_tags: string[] | null;
    platform: number[] | null;
  }
}> => {
  try {
    const response = await api.get(`/public-user-game-status/${username}/get_public_statuses/`, {
      params: { game_ids: gameIds.join(',') }
    });
    return response.data;
  } catch (error) {
    console.error('パブリックゲームステータスの一括取得に失敗しました', error);
    return {};
  }
};

// 誕生日のみを取得する関数
export const getUserBirthDate = async (): Promise<string | null> => {
  try {
    const response = await api.get('/user/birth-date/');
    return response.data.birth_date;
  } catch (error) {
    console.error('誕生日の取得に失敗しました', error);
    return null;
  }
};

export const uploadGameImage = async (formData: FormData) => {
  const response = await api.post('/images/upload/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
    },
  });
  return response.data;
};

export const getGameImages = async (gameId: number, limit?: number, offset: number = 0) => {
  const params = new URLSearchParams();
  if (limit) params.append('limit', limit.toString());
  if (offset > 0) params.append('offset', offset.toString());
  
  const response = await api.get(`/games/${gameId}/images/?${params.toString()}`);
  return response.data;
};

export const getGameVideos = (gameId: number) => {
  return api.get(`/games/${gameId}/videos/`);
};

interface AddVideoData {
  game_id: number;
  youtube_id: string;
  title: string;
  description?: string;
  type: number;
  publish_date?: string | null;
}

export const addGameVideo = (data: AddVideoData) => {
  return api.post('/videos/upload/', data);
};

export const getGameDescription = (gameId: number, steamId?: string) => {
  return api.get(`/games/${gameId}/description/`, {
    params: { steam_id: steamId }
  });
};

interface AddDescriptionData {
  game_id: number;
  description: string | null;
  story: string | null;
}

export const addGameDescription = (data: AddDescriptionData) => {
  return api.post('/description/upload/', data);
};

// アバターアップロード用の関数を修正
export const uploadAvatar = async (formData: FormData): Promise<{ avatar_url: string }> => {
  const response = await api.post('/images/avatar/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// プロフィールヘッダー画像。サーバー側で 1280x540 の WebP に再エンコードされる。
export const uploadBanner = async (formData: FormData): Promise<{ banner: string }> => {
  const response = await api.post('/images/banner/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteBanner = async (): Promise<{ banner: null }> => {
  const response = await api.delete('/images/banner/');
  return response.data;
};

export const getUserActivities = (username?: string, page: number = 1) => {
  return api.get(username ? `/user-activities/${username}/?page=${page}` : `/user-activities/?page=${page}`);
};

// ProfileTab用の統計情報を取得
export const getProfileStats = async (username?: string) => {
  const endpoint = username ? 
    `/profile-stats/${username}/` : 
    '/profile-stats/';
  return api.get(endpoint);
};

// 最近プレイしたゲームを取得（プロフィール用）
export const getRecentPlayedGames = async (username?: string) => {
  const endpoint = username ?
    `/recent-games/?username=${username}` :
    '/recent-games/';
  return api.get(endpoint);
};

// プロフィール用のレビューを取得（最新2件）
export const getProfileReviews = async (username?: string) => {
  const endpoint = username ?
    `/profile-reviews/?username=${username}` :
    '/profile-reviews/';
  return api.get(endpoint);
};

export const getReviewDetail = async (gameId: number, reviewId: number): Promise<ReviewDetail> => {
  const response = await api.get(`/games/${gameId}/reviews/${reviewId}/`);
  return response.data;
};

export const likeReview = async (reviewId: number) => {
  const response = await api.post(`/reviews/${reviewId}/like/`);
  return {
    data: {
      count: response.data.count,
      like_count: response.data.like_count
    }
  };
};

export const unlikeReview = async (reviewId: number) => {
  const response = await api.delete(`/reviews/${reviewId}/like/`);
  return {
    data: {
      count: response.data.count,
      like_count: 0
    }
  };
};

export const toggleReviewHelpfulness = async (reviewId: number, isHelpful: boolean) => {
  return api.post(`/reviews/${reviewId}/helpfulness/`, { is_helpful: isHelpful });
};

export const removeReviewHelpfulness = async (reviewId: number) => {
  return api.delete(`/reviews/${reviewId}/helpfulness/`);
};

export const getReleaseCalendar = async (date?: string | null) => {
  const response = await api.get('/release-calendar/' + (date ? `?date=${date}` : ''));
  return response.data;
};

export const getWeeklyReleaseCalendar = async (date?: string | null, limit?: number) => {
  const params = new URLSearchParams();
  if (date) params.append('date', date);
  if (limit) params.append('limit', limit.toString());
  
  const response = await api.get('/weekly-release-calendar/' + (params.toString() ? `?${params.toString()}` : ''));
  return response.data;
};

// プラットフォーム一覧のみを取得する軽量な関数
export const getGamePlatforms = async (gameId: number): Promise<Platform[]> => {
  try {
    const response = await api.get(`/games/${gameId}/platforms/`);
    return response.data;
  } catch (error) {
    console.error('プラットフォーム一覧の取得に失敗しました', error);
    return [];
  }
};

export const getGamesByKeyword = async (id: string, page: number = 1) => {
  const response = await axios.get(`${API_BASE_URL}/games/by_keyword/?id=${id}&page=${page}`);
  return response.data;
};

export const handleDiscordCallback = async (code: string) => {
  try {
    const response = await api.post('/auth/discord/callback/', { code });
    
    // トークンを保存
    localStorage.setItem('token', response.data.access_token);
    localStorage.setItem('refreshToken', response.data.refresh_token);
    
    // ユーザー情報を保存
    localStorage.setItem('user', JSON.stringify(response.data.user));
    
    return response.data;
  } catch (error) {
    console.error('Discord login failed', error);
    throw error;
  }
};

export const handleXboxLink = async (code: string) => {
  try {
    const response = await api.post('/auth/xbox/callback/', { code });
    return response.data;
  } catch (error) {
    console.error('Xbox link failed', error);
    throw error;
  }
};

export const getReviewsByUser = async (
  username: string, 
  page: number = 1, 
  query: string = '',
  order: string = 'default'
) => {
  const params = new URLSearchParams({
    page: page.toString(),
  });

  if (query) {
    params.append('query', query);
  }
  if (order !== 'default') {
    params.append('order', order);
  }

  const response = await api.get(`/reviews/user/${username}/?${params.toString()}`);
  return response.data;
};

export const getNotifications = async (page: number = 1) => {
  try {
    const response = await api.get(`/notifications/?page=${page}`);
    return response.data;
  } catch (error) {
    console.error('通知の取得に失敗しました:', error);
    return {
      results: [],
      count: 0,
      next: false,
      previous: false
    };
  }
};

export const markNotificationsAsRead = async () => {
  try {
    const response = await api.post('/notifications/mark-as-read/');
    return response.data;
  } catch (error) {
    console.error('通知の既読処理に失敗しました:', error);
    throw error;
  }
};

export const getUnreadNotificationCount = async (): Promise<{ count: number; has_more: boolean }> => {
  try {
    const response = await api.get('/notifications/unread-count/');
    return response.data;
  } catch (error) {
    console.error('未読通知数の取得に失敗しました:', error);
    return { count: 0, has_more: false };
  }
};

export const getPopularGamesByYearStatus = (year: number, limit: number = 30, offset: number = 0): Promise<{ data: { results: Game[], next: number | null, year: number } }> => 
  api.get(`/popular-games-by-year-status/?year=${year}&limit=${limit}&offset=${offset}`);

// フォロー関連の関数
export const followUser = async (username: string) => {
  try {
    const response = await api.post(`/follow/${username}/`);
    return response.data;
  } catch (error) {
    console.error('フォローに失敗しました:', error);
    throw error;
  }
};

export const unfollowUser = async (username: string) => {
  try {
    const response = await api.delete(`/follow/${username}/`);
    return response.data;
  } catch (error) {
    console.error('フォロー解除に失敗しました:', error);
    throw error;
  }
};

// フォロワー一覧を取得
export const getUserFollowers = async (username: string, page: number = 1, pageSize: number = 10) => {
  const response = await api.get(`/${username}/followers/?page=${page}&page_size=${pageSize}`);
  return response.data;
};

// フォロー中一覧を取得
export const getUserFollowing = async (username: string, page: number = 1, pageSize: number = 10) => {
  const response = await api.get(`/${username}/following/?page=${page}&page_size=${pageSize}`);
  return response.data;
};

export const getTimeline = async (page: number = 1) => {
  try {
    const response = await api.get(`/timeline/?page=${page}`);
    return response.data;
  } catch (error) {
    console.error('タイムラインの取得に失敗しました:', error);
    throw error;
  }
};

export const getActivityDetail = async (userId: number, activityId: number) => {
  const response = await api.get(`/activity/${userId}/${activityId}/`);
  return response.data;
};

export const getPublicTimeline = async () => {
  try {
    const response = await api.get('/public-timeline/');
    return response.data;
  } catch (error) {
    console.error('パブリックタイムラインの取得に失敗しました:', error);
    throw error;
  }
};

export const getOnlineUsersCount = async (): Promise<{ count: number }> => {
  const response = await api.get('/online-users-count/');
  return response.data;
};

export const startPlaying = (gameId: number) => 
  api.post('/play-session/', { game_id: gameId });

export const stopPlaying = (category?: string[], comment?: string) => 
  api.delete('/play-session/', { data: { category, comment } });

export const getPlayingStatus = () => 
  api.get('/play-session/');

export const cancelPlaying = () => 
  api.post('/play-session/cancel/');

export const getLatestPlayRecord = () =>
  api.get('/play-session/latest-record/');

export const getGamePlayLog = async (gameId: number, username?: string) => {
  const endpoint = username 
    ? `/games/${gameId}/playlog/${username}/`
    : `/games/${gameId}/playlog/`;
  const response = await api.get(endpoint);
  return response.data;
};

export const getUserPlayLogs = async (username?: string, page: number = 1) => {
  try {
    const endpoint = username ? 
      `/user-playlogs/${username}/?page=${page}` : 
      `/user-playlogs/?page=${page}`;
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    console.error('プレイログの取得に失敗しました:', error);
    return {
      results: [],
      count: 0
    };
  }
};

export const updatePlayLogSummary = async (gameId: number, summary: string) => {
  const response = await api.patch(`/games/${gameId}/playlog/summary/`, { summary });
  return response.data;
};

export const updatePlayLogRecord = async (gameId: number, recordData: {
  date: string;
  play_time: number;
  category: string[];
  comment: string;
}) => {
  const response = await api.patch(`/games/${gameId}/playlog/update_record/`, recordData);
  return response.data;
};

export const deletePlayLogRecord = async (gameId: number, date: string) => {
  const response = await api.delete(`/games/${gameId}/playlog/delete_record/`, {
    data: { date }
  });
  return response.data;
};

export const deletePlayLog = async (gameId: number) => {
  const response = await api.delete(`/games/${gameId}/playlog/delete/`);
  return response.data;
};

interface UserPersonalize {
  username: string;
  display_name: string | null;
  avatar: string | null;
}

export const getUserPersonalize = async (): Promise<UserPersonalize> => {
  try {
    const response = await api.get('/user/personalize/');
    return response.data;
  } catch (error) {
    console.error('ユーザー情報の取得に失敗しました:', error);
    return {
      username: '',
      display_name: null,
      avatar: null
    };
  }
};

export const getRevealedGames = (): Promise<{ data: { results: Game[]; next: number | null } }> =>
  api.get('/revealed-games/');

// ホーム機能紹介セクション（公開・ホームでのみ取得）
export const getHomeFeatures = (): Promise<import('../types/homeFeatures').HomeFeaturesConfig> =>
  api.get('/home-features/').then(r => r.data);

export const syncSteamLibrary = async () => {
  try {
    const response = await api.post('/user/steam/sync/');
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Steamライブラリの同期に失敗しました');
  }
};

export const checkSteamSyncStatus = async () => {
  try {
    const response = await api.get('/user/steam/status/');
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Steam同期ステータスの取得に失敗しました');
  }
};

// Steam自動同期設定
export interface SteamSyncSettings {
  auto_sync: boolean;
  sync_wishlist: boolean;
  organize_level: 'hybrid' | 'full';
  auto_full_when_dormant: boolean;
  promote_threshold_hours: number;
}

export const getSteamSyncSettings = async (): Promise<{
  settings: SteamSyncSettings;
  next_sync_time: string | null;
}> => {
  const response = await api.get('/user/steam/settings/');
  return response.data;
};

export const updateSteamSyncSettings = async (
  partial: Partial<SteamSyncSettings>
): Promise<{ settings: SteamSyncSettings }> => {
  try {
    const response = await api.patch('/user/steam/settings/', partial);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw new Error('自動同期設定の更新に失敗しました');
  }
};

export const syncPSNLibrary = async () => {
  try {
    const response = await api.post('/psn/sync/games/');
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('PSNライブラリの同期に失敗しました');
  }
};

export const checkPSNSyncStatus = async () => {
  try {
    const response = await api.get('/psn/sync/status/');
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('PSN同期ステータスの取得に失敗しました');
  }
};

// ステータスカウントの更新
export const updateStatusCounts = async (): Promise<{ message: string }> => {
  try {
    const response = await api.post('/user-profile/update-status-counts/');
    return response.data;
  } catch (error) {
    console.error('ステータスカウントの更新に失敗しました', error);
    throw error;
  }
};

export const syncXboxLibrary = async () => {
  try {
    const response = await api.post('/user/xbox/sync/');
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Xboxライブラリの同期に失敗しました');
  }
};

export const checkXboxSyncStatus = async () => {
  try {
    const response = await api.get('/user/xbox/status/');
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Xbox同期ステータスの取得に失敗しました');
  }
};

export const steamCallback = async (params: string) => {
  return await api.post('/auth/steam/callback/', {
    ...Object.fromEntries(new URLSearchParams(params))
  });
};

export const xboxCallback = async (code: string, mode?: string) => {
  return await api.post('/auth/xbox/callback/', {
    code,
    mode
  });
};

export const getEvent = async (eventId: number) => {
  const response = await api.get(`/events/${eventId}/`);
  return response.data;
};

export const getEventGames = async (eventId: number) => {
  const response = await api.get(`/events/${eventId}/games/`);
  return response.data;
};

export const getEventPickup = async () => {
  try {
    const response = await api.get('/events/pickup/');
    return response.data;
  } catch (error) {
    console.error('イベント情報の取得に失敗しました:', error);
    return [];
  }
};

// ニュース関連は services/newsApi.ts に集約（旧 getGameNews はそちらの
// fetchGameNews に置き換え済み。レスポンス形が {news:[]} から {results:[]} に変わっている）

export const setGamePriority = async (gameId: number, priority: number): Promise<{ game_id: number; priority: number }> =>
  api.post('/user-game-status/set_priority/', { game_id: gameId, priority });

export const setGameScore = async (gameId: number, score: number): Promise<{ game_id: number; score: number }> =>
  api.post('/user-game-status/set_score/', { game_id: gameId, score });

// メモリー機能のAPI
// エンドポイントの構成:
// - /user-memories/ : 個人のメモリー管理用（認証必要、DRFのrouter）
// - /memories/ : 公開メモリーの詳細表示用（認証不要、手動エンドポイント）
// - /public-memories/ : 公開メモリーの一覧用（認証不要、DRFのrouter）

export const getUserMemories = async (
  username: string,
  page: number = 1,
  query: string = '',
  order: string = 'default'
) => {
  const params = new URLSearchParams({
    username,
    page: page.toString(),
    query,
    order
  });
  
  return api.get(`/public-memories/by_user/?${params.toString()}`);
};

export const getMemoryDetail = async (memoryId: number) => {
  return api.get(`/memories/${memoryId}/`);
};

export const createMemory = async (memoryData: {
  game: number;
  title: string;
  content: string;
  play_date?: string | null;
  is_public?: boolean;
  is_anonymous?: boolean;
}) => {
  return api.post('/user-memories/', memoryData);
};

export const getUserMemoryByGame = async (gameId: number) => {
  try {
    const response = await api.get(`/user-memories/by_game/?game_id=${gameId}`);
    return response.data.results.length > 0 ? response.data.results[0] : null;
  } catch (error) {
    return null;
  }
};

export const updateMemory = async (memoryId: number, memoryData: {
  title?: string;
  content?: string;
  play_date?: string | null;
  is_public?: boolean;
  is_anonymous?: boolean;
}) => {
  return api.patch(`/user-memories/${memoryId}/`, memoryData);
};

export const deleteMemory = async (memoryId: number) => {
  return api.delete(`/user-memories/${memoryId}/`);
};

export const toggleMemoryLike = async (memoryId: number) => {
  return api.post(`/memories/${memoryId}/like/`);
};

export const getMemoryComments = async (memoryId: number, page: number = 1) => {
  return api.get(`/memories/${memoryId}/comments/?page=${page}`);
};

export const createMemoryComment = async (memoryId: number, content: string, isPublic: boolean = true) => {
  return api.post(`/memories/${memoryId}/comments/`, {
    content,
    is_public: isPublic
  });
};

// サブスクリプション関連のAPI関数
export const getSubscriptionStatus = async () => {
  return api.get('/subscription/status/');
};

export const createSubscription = async (data: { plan_type: number; billing_period?: string }) => {
  return api.post('/subscription/create/', data);
};

export const changeSubscription = async (data: { plan_type: number; billing_period?: string }) => {
  return api.post('/subscription/change/', data);
};

export const previewSubscriptionChange = async (data: { plan_type: number; billing_period?: string }) => {
  return api.post('/subscription/preview/', data);
};

export const cancelSubscription = async () => {
  return api.post('/subscription/cancel/');
};

export const uncancelSubscription = async () => {
  return api.post('/subscription/uncancel/');
};

export const cancelScheduledChange = async () => {
  return api.post('/subscription/cancel-scheduled-change/');
};

export const restoreSubscription = async () => {
  return api.post('/subscription/restore/');
};

export const createCustomerPortalSession = async () => {
  return api.post('/subscription/portal/');
};

export const getAvailablePlans = async () => {
  return api.get('/subscription/plans/');
};

export const getPricingPage = async () => {
  return api.get('/pricing-page/');
};

export const getAdminPricingPage = async () => {
  return api.get('/admin/pricing-page/');
};

export const updateAdminPricingPage = async (data: any) => {
  return api.put('/admin/pricing-page/', data);
};

// PSN関連のAPI関数
export const getPSNLinkStatus = async () => {
  const response = await api.get('/psn/link/');
  return response.data;
};

export const linkPSNAccount = async (psnId: string, verificationCode: string) => {
  const response = await api.post('/psn/link/', {
    psn_id: psnId,
    verification_code: verificationCode
  });
  return response.data;
};

export const unlinkPSNAccount = async () => {
  const response = await api.delete('/psn/link/');
  return response.data;
};

// 実績関連のAPI関数
export const getGameAchievements = async (gameId: number, platform?: string, showHidden?: boolean) => {
  const params = new URLSearchParams();
  if (platform) params.append('platform', platform);
  if (showHidden) params.append('show_hidden', 'true');
  
  const response = await api.get(`/games/${gameId}/achievements/?${params.toString()}`);
  return response.data;
};

export const getAchievementPlatforms = async (gameId: number) => {
  const response = await api.get(`/games/${gameId}/achievement-platforms/`);
  return response.data;
};

// 汎用実績関数は削除（現在はPSNのみ対応）

export const getPSNTrophies = async (gameId: number, showHidden?: boolean) => {
  const params = new URLSearchParams();
  params.append('game_id', gameId.toString());
  if (showHidden) params.append('show_hidden', 'true');
  
  const response = await api.get(`/psn-trophies/by_game/?${params.toString()}`);
  return response.data;
};

export const getPSNTrophySummary = async (gameId: number) => {
  const response = await api.get(`/psn-trophies/summary/?game_id=${gameId}`);
  return response.data;
};

// Steam実績取得
export const getSteamAchievements = async (gameId: number, showHidden?: boolean) => {
  const params = new URLSearchParams();
  params.append('platform', 'steam');
  if (showHidden) params.append('show_hidden', 'true');

  const response = await api.get(`/games/${gameId}/achievements/?${params.toString()}`);
  return response.data;
};

export const getGameHLTB = async (gameId: number): Promise<{
  hltb_id: number | null;
  name: string;
  main_story_hours: number | null;
  main_story_count: number | null;
  main_plus_extras_hours: number | null;
  main_plus_extras_count: number | null;
  completionist_hours: number | null;
  completionist_count: number | null;
  all_styles_hours: number | null;
  all_styles_count: number | null;
  similarity: number | null;
}> => {
  try {
    const response = await api.get(`/games/${gameId}/hltb/`);
    return response.data;
  } catch (error) {
    console.error('HLTBデータの取得に失敗しました:', error);
    throw error;
  }
};

export const getGameMetacritic = async (gameId: number): Promise<{
  slug: string;
  platform: string;
  metascore: number | null;
  metascore_count: number | null;
  user_score: number | null;
  user_score_count: number | null;
}> => {
  try {
    const response = await api.get(`/games/${gameId}/metacritic/`);
    return response.data;
  } catch (error) {
    console.error('Metacriticデータの取得に失敗しました:', error);
    throw error;
  }
};

export const getGameScores = async (gameId: number): Promise<{
  metacritic: {
    slug: string | null;
    platform: string | null;
    metascore: number | null;
    metascore_count: number | null;
    user_score: number | null;
    user_score_count: number | null;
  } | null;
  hltb: {
    hltb_id: number | null;
    review_score: number | null;
    review_count: number | null;
  } | null;
}> => {
  try {
    const response = await api.get(`/games/${gameId}/scores/`);
    return response.data;
  } catch (error) {
    console.error('スコアデータの取得に失敗しました:', error);
    throw error;
  }
};

// SteamChartsデータを取得する関数
export const getSteamChartData = async (steamId: string): Promise<[number, number][]> => {
  try {
    const response = await api.get(`/steam/charts/${steamId}/`);
    return response.data;
  } catch (error) {
    console.error('SteamChartsデータの取得に失敗しました:', error);
    throw error;
  }
};

// ゲームのアクティビティフィードを取得
export const getGameActivities = async (gameId: number, status?: number, page?: number, pageSize?: number) => {
  try {
    const params: Record<string, string | number> = {};
    if (status) params.status = status;
    if (page) params.page = page;
    if (pageSize) params.page_size = pageSize;
    const response = await api.get(`/games/${gameId}/activities/`, { params });
    return response.data;
  } catch (error) {
    console.error('ゲームアクティビティの取得に失敗しました:', error);
    throw error;
  }
};

// ゲームを記録しているユーザー一覧を取得
export const getGameUsers = async (gameId: number, status?: number, page?: number, pageSize?: number) => {
  try {
    const params: Record<string, string | number> = {};
    if (status) params.status = status;
    if (page) params.page = page;
    if (pageSize) params.page_size = pageSize;
    const response = await api.get(`/games/${gameId}/users/`, { params });
    return response.data;
  } catch (error) {
    console.error('ゲームユーザーの取得に失敗しました:', error);
    throw error;
  }
};

// プレイログリンクマネージャー関連のAPI
export interface PlayLogLink {
  title: string;
  url: string;
}

// プレイログのリンク一覧を取得
export const getPlayLogLinks = async (gameId: number): Promise<{ data: { data: PlayLogLink[] } }> => {
  try {
    const response = await api.get(`/games/${gameId}/playlog/links/`);
    return response;
  } catch (error) {
    console.error('プレイログリンクの取得に失敗しました:', error);
    throw error;
  }
};

// プレイログのリンク一覧を更新
export const updatePlayLogLinks = async (gameId: number, links: PlayLogLink[]): Promise<{ data: PlayLogLink[] }> => {
  try {
    const response = await api.patch(`/games/${gameId}/playlog/links/`, { links });
    return response;
  } catch (error) {
    console.error('プレイログリンクの更新に失敗しました:', error);
    throw error;
  }
};

// ゲームメモを取得
export const getGameMemo = async (gameId: number): Promise<{ data: { memo: string; updated_at?: string | null; created_at?: string | null } }> => {
  try {
    const response = await api.get(`/games/${gameId}/memo/`);
    return response;
  } catch (error) {
    console.error('メモの取得に失敗しました:', error);
    throw error;
  }
};

// ゲームメモを保存・更新
export const saveGameMemo = async (gameId: number, memo: string): Promise<{ data: { memo: string } }> => {
  try {
    const response = await api.post(`/games/${gameId}/memo/`, { memo });
    return response;
  } catch (error) {
    console.error('メモの保存に失敗しました:', error);
    throw error;
  }
};

// ゲームメモを削除
export const deleteGameMemo = async (gameId: number): Promise<void> => {
  try {
    await api.delete(`/games/${gameId}/memo/`);
  } catch (error) {
    console.error('メモの削除に失敗しました:', error);
    throw error;
  }
};

// プレイタイム詳細を取得
export const getGamePlaytimes = async (gameId: number): Promise<{ data: any[] }> => {
  try {
    const response = await api.get(`/games/${gameId}/playtime/`);
    return response;
  } catch (error) {
    console.error('プレイタイム詳細の取得に失敗しました:', error);
    throw error;
  }
};

// プレイタイム詳細を保存・更新
export const saveGamePlaytime = async (
  gameId: number,
  playstyle: number,
  playTimeMinutes: number,
  note: string
): Promise<{ data: any }> => {
  try {
    const response = await api.post(`/games/${gameId}/playtime/`, {
      playstyle,
      play_time_minutes: playTimeMinutes,
      note
    });
    return response;
  } catch (error) {
    console.error('プレイタイム詳細の保存に失敗しました:', error);
    throw error;
  }
};

// プレイタイム詳細を削除
export const deleteGamePlaytime = async (gameId: number, playstyle: number): Promise<void> => {
  try {
    await api.delete(`/games/${gameId}/playtime/`, { data: { playstyle } });
  } catch (error) {
    console.error('プレイタイム詳細の削除に失敗しました:', error);
    throw error;
  }
};

// リリースノート一覧を取得
export const getReleaseNotes = async () => {
  try {
    const response = await api.get('/release-notes/');
    return response.data;
  } catch (error) {
    console.error('リリースノートの取得に失敗しました:', error);
    throw error;
  }
};

// 最新のポップアップ用リリースノートを取得
export const getLatestPopupReleaseNote = async () => {
  try {
    const response = await api.get('/release-notes/latest-popup/');
    return response.data;
  } catch (error: any) {
    // 204 No Content の場合は null を返す
    if (error.response?.status === 204) {
      return null;
    }
    console.error('最新リリースノートの取得に失敗しました:', error);
    throw error;
  }
};

// ヘッダーUI状態を一括取得（リリースノートのバッジ/ポップアップ用）
// 【設計方針】今後お知らせ機能を追加する場合もこのレスポンスに統合する。
// フロント側では各項目のIDをlocalStorageと比較して既読/未読を判定する。
export const getHeaderState = async () => {
  try {
    const response = await api.get('/header-state/');
    return response.data;
  } catch (error) {
    console.error('ヘッダー状態の取得に失敗しました:', error);
    return { latest_release_note: null, latest_popup_release_note: null };
  }
};

// アクティビティリアクション関連のAPI
export const addActivityReaction = async (activityId: number, reactionType: number) => {
  try {
    const response = await api.post(`/activities/${activityId}/reaction/`, {
      reaction_type: reactionType
    });
    return response.data;
  } catch (error) {
    console.error('リアクションの追加に失敗しました:', error);
    throw error;
  }
};

export const removeActivityReaction = async (activityId: number) => {
  try {
    const response = await api.delete(`/activities/${activityId}/reaction/`);
    return response.data;
  } catch (error) {
    console.error('リアクションの削除に失敗しました:', error);
    throw error;
  }
};

// ============================================
// ポスト（つぶやき）
// ============================================

export const getUserPosts = async (page: number = 1, filters?: { search?: string; game_id?: number; date_from?: string; date_to?: string }) => {
  const params = new URLSearchParams({ page: String(page) });
  if (filters?.search) params.set('search', filters.search);
  if (filters?.game_id) params.set('game_id', String(filters.game_id));
  if (filters?.date_from) params.set('date_from', filters.date_from);
  if (filters?.date_to) params.set('date_to', filters.date_to);
  const response = await api.get(`/user-posts/?${params}`);
  return response.data;
};

export const createPost = async (data: { content: string; game?: number | null; visibility?: number }) => {
  const response = await api.post('/user-posts/', data);
  return response.data;
};

export const editPost = async (postId: number, data: { content: string; game?: number | null; visibility?: number }) => {
  const response = await api.patch(`/user-posts/${postId}/`, data);
  return response.data;
};

export const deletePost = async (postId: number) => {
  const response = await api.delete(`/user-posts/${postId}/`);
  return response.data;
};

export const getPublicPosts = async (page: number = 1) => {
  const response = await api.get(`/public-posts/?page=${page}`);
  return response.data;
};

export const getPostsByGame = async (gameId: number, page: number = 1, sort: string = 'latest') => {
  const response = await api.get(`/public-posts/by_game/?game_id=${gameId}&page=${page}&sort=${sort}`);
  return response.data;
};

export const getPostsByUser = async (username: string, page: number = 1, filters?: { search?: string; game_id?: number; date_from?: string; date_to?: string }) => {
  const params = new URLSearchParams({ username, page: String(page) });
  if (filters?.search) params.set('search', filters.search);
  if (filters?.game_id) params.set('game_id', String(filters.game_id));
  if (filters?.date_from) params.set('date_from', filters.date_from);
  if (filters?.date_to) params.set('date_to', filters.date_to);
  const response = await api.get(`/public-posts/by_user/?${params}`);
  return response.data;
};

export const getPostsFeed = async (page: number = 1) => {
  const response = await api.get(`/public-posts/feed/?page=${page}`);
  return response.data;
};

export const addPostReaction = async (postId: number, reactionType: number) => {
  const response = await api.post(`/posts/${postId}/reaction/`, { reaction_type: reactionType });
  return response.data;
};

export const removePostReaction = async (postId: number) => {
  const response = await api.delete(`/posts/${postId}/reaction/`);
  return response.data;
};

// アナウンスバナー取得
export const getAnnouncementBanner = async () => {
  try {
    const response = await api.get('/announcement-banner/');
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 204) {
      return null;
    }
    return null;
  }
};

// 検索ページメッセージ取得
export const getSearchMessages = async (mediaType?: string) => {
  try {
    const params = mediaType ? { media: mediaType } : {};
    const response = await api.get('/search-messages/', { params });
    return response.data;
  } catch {
    return mediaType ? [] : { game: [], anime: [], movie: [] };
  }
};

export default api;
